/**
 * src/hooks/useBookingPayment.ts
 *
 * Correções e melhorias:
 * - Exporta named hook `useBookingPayment`.
 * - Tipagem explícita para PaymentState.
 * - Tratamento robusto de erros e mensagens claras.
 * - loadPaymentStatus valida establishmentId e retorna boolean.
 * - payWithCard / payWithDebit / payWithPix encapsulam chamadas ao service.
 * - Não altera contratos backend; usa helpers de bookingPaymentClient.
 *
 * Uso:
 * import { useBookingPayment } from "src/hooks/useBookingPayment";
 */

import { useCallback, useState } from "react";
import {
  payWithCard as servicePayWithCard,
  payWithPix as servicePayWithPix,
  getOnlinePaymentsStatus,
} from "../../../services/bookingPaymentClient";
import { getRefundFeePercentage } from "../../../services/establishments";

export type PaymentState = {
  loading: boolean;
  approved: boolean;
  failed: boolean;
  error?: string | null;
  confirmation?: any | null;
};

export function useBookingPayment() {
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState<boolean | null>(
    null
  );
  const [paymentState, setPaymentState] = useState<PaymentState>({
    loading: false,
    approved: false,
    failed: false,
    error: null,
    confirmation: null,
  });
  const [refundFeePercentage, setRefundFeePercentage] = useState<number | null>(null);

  /**
   * loadPaymentStatus
   * - Consulta backend para verificar se pagamentos online estão habilitados.
   * - Atualiza estado local e retorna boolean.
   */
  const loadPaymentStatus = useCallback(
    async (establishmentId?: string | null, failOnError = false): Promise<boolean> => {
      if (!establishmentId) {
        setOnlinePaymentsEnabled(false);
        return false;
      }
      try {
        const res: any = await getOnlinePaymentsStatus(establishmentId);
        const enabled = !!(
          res?.onlinePaymentsEnabled ??
          res?.onlinePaymentEnabled ??
          res?.enabled ??
          false
        );
        setOnlinePaymentsEnabled(enabled);
        return enabled;
      } catch (err: any) {
        if (failOnError) {
          setOnlinePaymentsEnabled(null);
          throw new Error(
            "Não foi possível verificar a forma de pagamento. Tente novamente antes de confirmar."
          );
        }
        console.error("useBookingPayment.loadPaymentStatus error:", err);
        setOnlinePaymentsEnabled(null);
        throw err;
      }
    },
    []
  );

  const resetPayment = () => {
    setPaymentState({
      loading: false,
      approved: false,
      failed: false,
      error: null,
      confirmation: null,
    });
  };

  /**
   * handleResponse
   * - Normaliza respostas do backend/service.
   * - Atualiza paymentState e lança erro se falha.
   */
  const handleResponse = async (resp: any) => {
    if (!resp) {
      setPaymentState((s) => ({
        ...s,
        loading: false,
        failed: true,
        error: "Resposta inválida do provedor",
      }));
      throw new Error("Resposta inválida do provedor");
    }

    // Pagamento aprovado
    if (
      resp?.status === "paid" ||
      resp?.status === "approved" ||
      resp?.status === "authorized" ||
      !!resp?.providerPaymentId
    ) {
      setPaymentState((s) => ({
        ...s,
        loading: false,
        approved: true,
        failed: false,
        confirmation: resp,
        error: null,
      }));
      return resp;
    }

    // PIX iniciado (status pending / in_process)
    if (
      resp?.pix &&
      (resp?.status === "pending" ||
        resp?.status === "in_process" ||
        resp?.status === "waiting_payment" ||
        resp?.status === "processing" ||
        !resp?.status)
    ) {
      setPaymentState((s) => ({
        ...s,
        loading: false,
        approved: false,
        failed: false,
        confirmation: resp,
        error: null,
      }));
      return resp;
    }

    // Falha padrão
    const errMsg = resp?.error || resp?.message || "Pagamento não aprovado";
    setPaymentState((s) => ({ ...s, loading: false, failed: true, error: errMsg }));
    throw new Error(errMsg);
  };

  /**
   * payWithCard
   * - payload deve seguir contrato do backend /payments/card
   */
  const payWithCard = async (payload: any) => {
    if (onlinePaymentsEnabled === false) {
      const msg = "Pagamentos online não habilitados";
      setPaymentState((s) => ({ ...s, loading: false, failed: true, error: msg }));
      throw new Error(msg);
    }

    setPaymentState((s) => ({ ...s, loading: true, error: null, failed: false }));

    try {
      const resp = await servicePayWithCard(payload);
      return await handleResponse(resp);
    } catch (err: any) {
      const msg = err?.message || "Erro ao processar pagamento com cartão";
      setPaymentState((s) => ({ ...s, loading: false, failed: true, error: msg }));
      throw err;
    }
  };

  /**
   * payWithDebit
   * - Reaproveita endpoint de cartão, definindo paymentMethodId como debit_card
   */
  const payWithDebit = async (payload: any) => {
    if (onlinePaymentsEnabled === false) {
      const msg = "Pagamentos online não habilitados";
      setPaymentState((s) => ({ ...s, loading: false, failed: true, error: msg }));
      throw new Error(msg);
    }

    setPaymentState((s) => ({ ...s, loading: true, error: null, failed: false }));

    try {
      const p = { ...payload, paymentMethodId: payload.paymentMethodId || "debit_card" };
      const resp = await servicePayWithCard(p);
      return await handleResponse(resp);
    } catch (err: any) {
      const msg = err?.message || "Erro ao processar pagamento débito";
      setPaymentState((s) => ({ ...s, loading: false, failed: true, error: msg }));
      throw err;
    }
  };

  /**
   * payWithPix
   * - payload deve seguir contrato do backend /establishment/pay-pix
   */
  const payWithPix = async (payload: any) => {
    if (onlinePaymentsEnabled === false) {
      const msg = "Pagamentos online não habilitados";
      setPaymentState((s) => ({ ...s, loading: false, failed: true, error: msg }));
      throw new Error(msg);
    }

    setPaymentState((s) => ({ ...s, loading: true, error: null, failed: false }));

    try {
      const resp = await servicePayWithPix(payload);
      return await handleResponse(resp);
    } catch (err: any) {
      const msg = err?.message || "Erro ao processar pagamento PIX";
      setPaymentState((s) => ({ ...s, loading: false, failed: true, error: msg }));
      throw err;
    }
  };

  const loadRefundFeePercentage = useCallback(async (establishmentId?: string | null) => {
    if (!establishmentId) {
      setRefundFeePercentage(null);
      return null;
    }

    try {
      const res = await getRefundFeePercentage(establishmentId);
      const fee = Number(res?.refundFeePercentage);

      if (Number.isFinite(fee)) {
        setRefundFeePercentage(fee);
        return fee;
      }

      setRefundFeePercentage(null);
      return null;
    } catch (err) {
      console.error("useBookingPayment.loadRefundFeePercentage error:", err);
      setRefundFeePercentage(null);
      return null;
    }
  }, []);

  return {
    onlinePaymentsEnabled,
    paymentState,
    loadPaymentStatus,
    payWithCard,
    payWithDebit,
    payWithPix,
    resetPayment,
    setPaymentState,
    refundFeePercentage,
    loadRefundFeePercentage,
  };
}
